import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import styles from './style.module.scss'

/*
 * Filter nach Zielgruppe über allen drei Registern.
 *
 * Vorlagen an die Administration sind im Katalog auf zwei Arten notiert: die
 * älteren mit leerer Zielgruppe (niemand von außen bekommt sie), die neueren
 * ausdrücklich mit „Administration". Beides meint denselben Empfängerkreis und
 * bekommt deshalb **einen** Eintrag — ADMIN_KEY. Sonst stünde „Administration"
 * zweimal in der Leiste, und welcher der beiden Knöpfe was findet, wäre nicht
 * zu erkennen.
 *
 * Die Auswahl wird aus den tatsächlich vorhandenen Zielgruppen gebaut und nicht
 * fest verdrahtet: Kommt im Katalog eine Gruppe dazu, taucht sie hier von selbst
 * auf statt still zu fehlen.
 */

export const ADMIN_KEY = '__admin__'

// So heißt die Zielgruppe im Katalog, wenn sie ausdrücklich dasteht.
const ADMIN_GROUP = 'Administration'

// Reihenfolge der geläufigen Gruppen; alles Weitere wird alphabetisch angehängt.
const ORDER = ['SuS', 'LuL', 'Unterstützer:in']

/*
 * `items` wird abgesichert: Der Filter ist ein geteilter Baustein, und ein
 * Aufruf mit etwas anderem als einer Liste riss frueher die ganze Seite ab
 * ("Invalid attempt to iterate non-iterable instance"). Eine Filterleiste ohne
 * Einträge ist ein kleiner Schaden, eine weisse Seite ein grosser.
 */
// Leere Zielgruppe oder ausdrücklich „Administration": beides geht an die
// Administration selbst.
const isAdminTarget = groups => !groups.length || groups.includes(ADMIN_GROUP)

export const targetGroupOptions = items => {
	const known = new Set()
	let hasAdmin = false

	for (const item of Array.isArray(items) ? items : []) {
		const groups = item.targetGroups || []
		if (isAdminTarget(groups)) hasAdmin = true
		groups.filter(group => group !== ADMIN_GROUP).forEach(group => known.add(group))
	}

	const rest = [...known].filter(group => !ORDER.includes(group)).sort()
	const options = [...ORDER.filter(group => known.has(group)), ...rest]

	return hasAdmin ? [...options, ADMIN_KEY] : options
}

export const matchesTargetGroup = (item, selected) => {
	if (!selected) return true
	const groups = item.targetGroups || []
	if (selected === ADMIN_KEY) return isAdminTarget(groups)
	return groups.includes(selected)
}

/*
 * Mehrfachauswahl aus der Filterleiste: Eine Vorlage passt, sobald sie zu
 * *einer* der gewaehlten Zielgruppen gehoert. Ein Und waere hier die falsche
 * Frage — Vorlagen richten sich an mehrere Gruppen zugleich, und wer „SuS" und
 * „LuL" anhakt, will beide sehen und nicht nur die Schnittmenge.
 */
export const matchesTargetGroups = (item, selected) =>
	!selected?.length || selected.some(value => matchesTargetGroup(item, value))

export default function TargetGroupFilter({ options, value, onChange }) {
	const { t } = useTranslation()

	if (!options.length) return null

	return (
		<div className={styles.groupFilter}>
			<span className={styles.groupFilterLabel}>{t('Target group')}:</span>
			<button
				type="button"
				className={clsx(styles.groupChip, !value && styles.groupChipActive)}
				onClick={() => onChange('')}
			>
				{t('All')}
			</button>
			{options.map(option => (
				<button
					key={option}
					type="button"
					className={clsx(styles.groupChip, value === option && styles.groupChipActive)}
					onClick={() => onChange(value === option ? '' : option)}
				>
					{option === ADMIN_KEY ? t('Administration') : option}
				</button>
			))}
		</div>
	)
}

TargetGroupFilter.propTypes = {
	options: PropTypes.arrayOf(PropTypes.string).isRequired,
	value: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired
}
